import { FC } from 'react';
import {
  Stack,
  Button,
  Group,
  Text,
  Alert,
  LoadingOverlay,
  Badge,
  Box,
} from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { IconArrowLeft, IconAlertCircle, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useFormNavigationBlock } from '@hooks/useFormNavigationBlock';
import { ConfirmCloseDialog } from '@/components/ConfirmCloseDialog';
import { usePatientSubmit } from '@hooks/usePatientSubmit';
import { usePatientDemographicsForm } from '@hooks/usePatientDemographicsForm';
import { PatientEditDemographicsForm } from '@/components/forms/PatientEditDemographicsForm';
import dayjs from 'dayjs';

interface PatientReviewProps {
  patient: Patient | null;
  isNewPatient: boolean;
  onPatientSaved: (patient: Patient) => void;
  onContinueWithoutSave?: () => void;
  onDirtyStateChange?: (isDirty: boolean) => void;
  onBack: () => void;
}

export const PatientReview: FC<PatientReviewProps> = ({
  patient,
  isNewPatient,
  onPatientSaved,
  onContinueWithoutSave,
  onDirtyStateChange,
  onBack,
}) => {
  const { t } = useTranslation(['patient-onboarding', 'create-patient-modal']);

  const { onSubmit, loading } = usePatientSubmit({
    isNewPatient,
    onPatientSaved,
  });

  // Use the shared form hook
  const {
    form,
    requiredFields,
    relatedPersons,
    hasChanges,
    handleDiscardChanges,
    changedFieldsCount,
  } = usePatientDemographicsForm({
    patient,
    opened: true,
  });

  // Use form navigation blocking hook
  const {
    showNavigationModal,
    handleNavigationDiscard,
    handleNavigationCancel,
  } = useFormNavigationBlock({
    isDirty: hasChanges,
    onDiscard: handleDiscardChanges,
    onDirtyStateChange,
  });

  // Get last verified date
  const lastVerified = patient?.meta?.lastUpdated
    ? dayjs(patient.meta.lastUpdated).format('DD/MM/YYYY HH:mm')
    : null;

  // Handle continue without save (for existing patients with no changes)
  const handleContinueWithoutSave = () => {
    if (onContinueWithoutSave) {
      onContinueWithoutSave();
    }
  };

  // Handle discard changes and continue
  const handleDiscardAndContinue = () => {
    handleDiscardChanges();
    if (onContinueWithoutSave) {
      onContinueWithoutSave();
    }
  };

  return (
    <Stack gap="lg" pos="relative">
      <LoadingOverlay visible={loading} />

      <Box>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text size="xl" fw={700} mb="xs">
              {isNewPatient
                ? t('patient.titleNew')
                : t('patient.titleExisting')}
            </Text>

            {!isNewPatient && lastVerified && (
              <Text size="sm" c="dimmed">
                {t('patient.lastVerified', { date: lastVerified })}
              </Text>
            )}
          </Box>

          {changedFieldsCount > 0 && (
            <Badge
              size="lg"
              color={hasChanges ? 'orange' : 'green'}
              variant="light"
            >
              {hasChanges
                ? t('patient.changesDetected', { count: changedFieldsCount })
                : t('patient.noChanges')}
            </Badge>
          )}
        </Group>

        {!isNewPatient && !hasChanges && (
          <Alert color="blue" mt="sm">
            {t('patient.verificationNote')}
          </Alert>
        )}

        {hasChanges && (
          <Alert color="orange" mt="sm" icon={<IconAlertCircle size={16} />}>
            {t('patient.unsavedChangesWarning')}
          </Alert>
        )}
      </Box>

      <form
        onSubmit={form.onSubmit((values) =>
          onSubmit(values, patient, relatedPersons)
        )}
      >
        <Stack gap="md">
          <PatientEditDemographicsForm
            form={form}
            requiredFields={requiredFields}
          />

          <Group justify="space-between" mt="xl">
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBack}
              disabled={loading}
            >
              {t('encounter.backButton')}
            </Button>

            <Group gap="sm">
              {/* For existing patients with changes: show discard option */}
              {!isNewPatient && hasChanges && (
                <Button
                  variant="subtle"
                  color="gray"
                  leftSection={<IconX size={16} />}
                  onClick={handleDiscardAndContinue}
                  disabled={loading}
                >
                  {t('patient.discardButton')}
                </Button>
              )}

              {/* Primary action button */}
              {isNewPatient || hasChanges ? (
                <Button type="submit" loading={loading} size="md">
                  {t('patient.saveButton')}
                </Button>
              ) : (
                <Button
                  onClick={handleContinueWithoutSave}
                  disabled={loading}
                  size="md"
                >
                  {t('patient.continueButton')}
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </form>

      {/* Navigation blocking modal */}
      <ConfirmCloseDialog
        opened={showNavigationModal}
        onCancel={handleNavigationCancel}
        onConfirm={handleNavigationDiscard}
        size="md"
        closeOnClickOutside={true}
        closeOnEscape={true}
        zIndex={200}
      />
    </Stack>
  );
};
